/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { ClinicalCacheFactory } from './factory/clinical-cache-factory';
import { CacheFactoryService } from './factory/cache-factory.service';

@Injectable({
  providedIn: 'root'
})
export class PatientHistoryCategoriesService {
  private readonly patientHistoryCategoriesCache: ClinicalCacheFactory;
  private readonly topCount = 0;
  private categoryUpdateEvent = 'categoriesUpdate';
  private idMap = {} as any;
  private SIRCC_KEYS = ['reasonForExam', 'modality', 'bodyRegion'];

  constructor(private cacheFactoryService: CacheFactoryService, private appConfigService: AppConfigService) {
    this.patientHistoryCategoriesCache = this.cacheFactoryService.createClinicalCacheFactory(
      'patientCategoriesCache',
      this.getEndpointFunction(),
      this.comparator
    );
    this.topCount = this.appConfigService.getSIRCCTopCount();
  }

  getPatientHistoryCategories(patientId, requestedProcedureId, refreshIfCached, requestParams) {
    return this.patientHistoryCategoriesCache.get(patientId, refreshIfCached, requestParams).then(data => {
      return this.cacheFactoryService.idMappingAddHandler(this.idMap, data, patientId, requestedProcedureId);
    });
  }

  removePatientCategories(requestedProcedureId) {
    return this.patientHistoryCategoriesCache.remove(
      this.cacheFactoryService.idMappingRemoveHandler(this.idMap, requestedProcedureId)
    );
  }

  onPatientHistoryCategoriesUpdate(callback, context) {
    return this.patientHistoryCategoriesCache.on(this.categoryUpdateEvent, callback, context);
  }

  getPatientHistoryCategoriesCache() {
    return this.patientHistoryCategoriesCache;
  }

  /************************** Private Functions **************************/

  private shouldUseCache(serverStatus, cacheStatus) {
    return serverStatus !== 'SUCCESS' && cacheStatus !== 'FAILURE';
  }

  private areNumberOfDocumentsEqual(cachedDocuments, serverDocuments) {
    return cachedDocuments.length === serverDocuments.length;
  }

  private areLastRetrievalsEqual(cachedCategory, serverCategory) {
    return cachedCategory.lastRetrieval === serverCategory.lastRetrieval;
  }

  private shouldUpdateCategories(serverDataCategory, cachedDataCategory) {
    return (
      serverDataCategory.responseCode !== cachedDataCategory.responseCode ||
      !this.areNumberOfDocumentsEqual(cachedDataCategory.contents.documents, serverDataCategory.contents.documents) ||
      !this.areLastRetrievalsEqual(cachedDataCategory, serverDataCategory)
    );
  }

  private areDocumentIdsEqual(cachedDocument, serverDocument) {
    return cachedDocument.id === serverDocument.id;
  }

  private areDocumentAuthorsEqual(cachedDocument, serverDocument) {
    return _.isEqual(cachedDocument.authors, serverDocument.authors);
  }

  private areDocumentLastModifiedDatesEqual(cachedDocument, serverDocument) {
    return cachedDocument.lastModifiedDate === serverDocument.lastModifiedDate;
  }

  private areDocumentsDifferent(cachedDocuments, serverDocuments) {
    for (let i = 0; i < serverDocuments.length; i++) {
      if (
        !this.areDocumentIdsEqual(cachedDocuments[i], serverDocuments[i]) ||
        !this.areDocumentAuthorsEqual(cachedDocuments[i], serverDocuments[i]) ||
        !this.areDocumentLastModifiedDatesEqual(cachedDocuments[i], serverDocuments[i])
      ) {
        return true;
      }
    }
    return false;
  }

  private updateCategoriesIfNeeded(needed, patientId, serverData) {
    if (needed) {
      this.patientHistoryCategoriesCache.fireEvent(this.categoryUpdateEvent, patientId, serverData);
    }
  }

  private comparator = (patientId, cachedData, serverData) => {
    let needsUpdate = false;
    //Handle the edge case where MRN is not found
    const categoriesLength = serverData.categories ? serverData.categories.length : 0;

    if (!cachedData.categories) {
      needsUpdate = true;
    } else {
      for (let i = 0; i < categoriesLength; i++) {
        const cachedDocuments = cachedData.categories[i].contents.documents;
        const serverDocuments = serverData.categories[i].contents.documents;
        const useCache = this.shouldUseCache(
          serverData.categories[i].responseCode,
          cachedData.categories[i].responseCode
        );

        if (useCache) {
          if (!cachedData.categories[i].isDataStale) {
            needsUpdate = true;
            serverData.categories[i].isDataStale = true;
          }

          serverData.categories[i].contents = cachedData.categories[i].contents;
          serverData.categories[i].hasError = cachedData.categories[i].hasError;
          serverData.categories[i].lastRetrieval = cachedData.categories[i].lastRetrieval;
          serverData.categories[i].responseCode = cachedData.categories[i].responseCode;
        }

        if (needsUpdate) {
          continue;
        }

        if (this.shouldUpdateCategories(serverData.categories[i], cachedData.categories[i])) {
          needsUpdate = true;
          continue;
        }

        needsUpdate = this.areDocumentsDifferent(cachedDocuments, serverDocuments);
      }
    }

    this.updateCategoriesIfNeeded(needsUpdate, patientId, serverData);
    this.patientHistoryCategoriesCache.updateCache(patientId, serverData);
  };

  private getEndpointFunction(): (id, params) => string {
    return (id, requestParams) => {
      let endpointTemplate = 'patient-history/patients/{id}/categories';
      const endpointNeedleId = '{id}';
      endpointTemplate = this.buildEndpointTemplateWithValues(endpointTemplate, requestParams);
      endpointTemplate = endpointTemplate.replace(endpointNeedleId, id);
      return endpointTemplate;
    };
  }

  private buildEndpointTemplateWithValues(urlString, requestParams) {
    const keys = _.keys(requestParams);
    let i = 0;
    let delimiter = '';
    _.each(keys, key => {
      if (!_.isEmpty(requestParams[key])) {
        delimiter = i === 0 ? '?' : '&';
        urlString = urlString + delimiter + key + '=' + requestParams[key];
        i++;
      }
    });

    //If there are SIRCC parameter, add top query param
    if (!_.isEmpty(_.intersection(keys, this.SIRCC_KEYS))) {
      urlString = urlString + delimiter + 'top' + '=' + this.topCount;
    }

    return urlString;
  }
}
